/**
 * PanelEntityId — Array/String/Module 식별자 값 객체
 *
 * === 왜 필요한가 ===
 * 기존 코드에서 DB seq(`solarModuleSeq`), 화면 표시 번호(`moduleId`/`stringId`),
 * 복합 Map Key(`module_42`)가 이름만 비슷하고 의미가 각각 달라 혼란이 발생.
 * 이 객체로 셋을 명확히 구분하고 계층 구조(Array→String→Module)를 함께 담는다.
 *
 * === 필드 설명 ===
 * @property {number}      selfSeq         — DB seq (유니크, 변하지 않음) — 객체 구분·API lookup·Map key
 * @property {string|null} selfDisplayId   — 화면 표시 번호 (String 내 1-based 등) — 라벨·사용자 검색
 * @property {string}      entityType      — 'array' | 'string' | 'module'
 * @property {number|null} parentSeq       — 직계 부모 seq (module이라면 소속 string의 seq)
 * @property {string|null} parentDisplayId — 직계 부모 displayId
 * @property {number|null} rootSeq         — 최상위 Array seq (계층 전체 추적용)
 * @property {string}      context         — 이 ID가 **어디서** 사용되는지 추적 (용도별 혼동 방지)
 *
 * === 사용 규칙 ===
 * - **객체 구분·Map key**:              항상 `selfSeq`로 비교 (displayId 사용 금지)
 * - **화면 표시·사용자 검색**:           항상 `selfDisplayId` 사용
 * - **계층 이동·부모 탐색**:             `parentSeq` / `parentDisplayId` 사용
 */

const ENTITY_TYPES = new Set(['array', 'string', 'module']);

export class PanelEntityId {
  /**
   * @param {Object} config
   * @param {number} config.selfSeq       — DB seq (필수)
   * @param {string} [config.selfDisplayId] — 화면 표시 번호
   * @param {'array'|'string'|'module'} config.entityType — (필수)
   * @param {Object} [config.hierarchy]   — 계층 정보
   * @param {number} [config.hierarchy.parentSeq]
   * @param {string} [config.hierarchy.parentDisplayId]
   * @param {number} [config.hierarchy.rootSeq]
   * @param {string} [config.context]     — 사용 맥락 (기본값 'unknown')
   */
  constructor(config) {
    if (config == null) {
      throw new Error('PanelEntityId: config is required');
    }
    if (config.selfSeq == null) {
      throw new Error('PanelEntityId: selfSeq is required');
    }
    if (!ENTITY_TYPES.has(config.entityType)) {
      throw new Error(`PanelEntityId: invalid entityType "${config.entityType}" — must be array|string|module`);
    }

    // ── 자기 자신 ──
    this.selfSeq = Number(config.selfSeq);
    this.selfDisplayId = config.selfDisplayId != null ? String(config.selfDisplayId) : null;
    this.entityType = config.entityType;

    // ── 계층 정보 ──
    const hierarchy = config.hierarchy ?? {};
    this.parentSeq = hierarchy.parentSeq != null ? Number(hierarchy.parentSeq) : null;
    this.parentDisplayId = hierarchy.parentDisplayId != null ? String(hierarchy.parentDisplayId) : null;
    this.rootSeq = hierarchy.rootSeq != null ? Number(hierarchy.rootSeq) : null;

    // ── 사용 맥락 ──
    this.context = typeof config.context === 'string' ? config.context : 'unknown';
  }

  /** @returns {string} EntityManager Map key (`${type}_${seq}`) — 항상 selfSeq 기반 */
  get mapKey() {
    return `${this.entityType}_${this.selfSeq}`;
  }

  /** @returns {string} 화면 표시 라벨 ("Module 3", "String 1" 등) */
  get displayLabel() {
    const prefix = this.entityType.charAt(0).toUpperCase() + this.entityType.slice(1);
    return `${prefix} ${this.selfDisplayId ?? this.selfSeq}`;
  }

  /**
   * 같은 **객체**(DB seq 기준)인지 비교
   * @param {*} other
   * @returns {boolean}
   */
  equals(other) {
    return other instanceof PanelEntityId &&
      this.entityType === other.entityType &&
      this.selfSeq === other.selfSeq;
  }

  /**
   * 같은 **화면 표시 번호**(displayId 기준)인지 비교
   * 라벨·검색 등 UI 비교용
   * @param {*} other
   * @returns {boolean}
   */
  displayEquals(other) {
    return other instanceof PanelEntityId &&
      this.entityType === other.entityType &&
      this.selfDisplayId != null &&
      other.selfDisplayId != null &&
      this.selfDisplayId === other.selfDisplayId;
  }

  /** @returns {string} */
  toString() {
    return `PanelEntityId(${this.entityType}:${this.selfSeq}${this.selfDisplayId != null ? '#' + this.selfDisplayId : ''})[${this.context}]`;
  }

  /** @returns {{ seq: number, displayId: string|null, type: string }} 직렬화 (스토어 저장용) */
  toJSON() {
    return {
      seq: this.selfSeq,
      displayId: this.selfDisplayId,
      type: this.entityType,
      context: this.context,
      parentSeq: this.parentSeq,
      parentDisplayId: this.parentDisplayId,
      rootSeq: this.rootSeq
    };
  }

  // ── 정적 팩토리 메서드 ──

  /** 서버 응답 데이터 → PanelEntityId */
  static fromServerData(data, entityType, context) {
    return new PanelEntityId({
      selfSeq: data.id ?? data.seq,
      selfDisplayId: data.displayId ?? data.name,
      entityType,
      hierarchy: {
        // parentSeq는 DB seq 전용 — 표시용 arrayId/stringId를 넣지 않는다
        parentSeq: data.parentId ?? null,
        parentDisplayId: data.parentDisplayId ?? null,
        rootSeq: data.rootSeq ?? null
      },
      context
    });
  }

  /** 트리 노드 → PanelEntityId */
  static fromTreeNode(node, context) {
    const raw = node.solarModuleSeq ?? node.solarStringSeq ?? node.solarArraySeq ?? node.seq ?? node.id;
    const seq = typeof raw === 'number' ? raw : parseInt(String(raw).replace(/^(array|string|module)_/, ''), 10);
    return new PanelEntityId({
      selfSeq: isNaN(seq) ? -1 : seq,
      // 표시용 필드 우선 — node.solarArrayId는 트리에서 DB seq를 담고 있으므로 마지막 폴백으로만 사용
      selfDisplayId: node.solarModuleId ?? node.moduleId ?? node.solarStringId ?? node.stringId ?? node.arrayId ?? node.solarArrayId,
      entityType: node.type,
      hierarchy: {
        parentSeq: node.parentSeq ?? node.stringSeq ?? null,
        parentDisplayId: node.parentDisplayId ?? node.stringId ?? null,
        rootSeq: node.rootSeq ?? node.arraySeq ?? null
      },
      context
    });
  }
}

export default PanelEntityId;
